/**
 * @sw-package checkout
 */
import Plugin from 'src/plugin-system/plugin.class';

export default class FriendlyCaptchaPlugin extends Plugin {
    static options = {
        siteKey: null,
        startMode: null,
        language: null,
        apiEndpoint: null,
        apiVersion: 'v1',
        containerSelector: '.frc-captcha',
        solutionFieldName: 'frc-captcha-solution',
        errorClass: 'has-error',
        successClass: 'has-success',
        invisible: false,
        hiddenClass: 'frc-captcha-hidden',
        invisibleShowAfterLongWaitMs: 5000,
    };

    init() {
        this._createCallbacks();
        this._form = this.el.nodeName === 'FORM' ? this.el : this.el.closest('form');
        this._invisibleShowTimeout = null;

        if (!this._form || !this.options.siteKey) {
            return;
        }

        this._container = this.el.querySelector(this.options.containerSelector);
        if (!this._container) {
            return;
        }

        if ((this.options.apiVersion || 'v1').toLowerCase() === 'v2') {
            this._initV2();

            return;
        }

        this._initV1();
    }

    _initV1() {
        if (!window.friendlyChallenge) {
            return;
        }

        if (this._widget?.valid) {
            return;
        }

        this._form.addEventListener('submit', this._callbacks.onFormSubmit);

        this._widget = new window.friendlyChallenge.WidgetInstance(this._container, {
            siteKey: this.options.siteKey,
            startMode: this.options.startMode,
            language: this.options.language,
            doneCallback: this._callbacks.onV1Done,
            readyCallback: this._callbacks.onV1Ready,
        });
    }

    _initV2() {
        if (!window.frcaptcha) {
            return;
        }

        if (this.options.apiEndpoint) {
            this._container.dataset.apiEndpoint = this.options.apiEndpoint;
        }

        const widgets = window.frcaptcha.attach([this._container]) || [];
        this._widget = widgets[0] || window.frcaptcha.getAllWidgets().find((w) => {
            return w.getElement && w.getElement() === this._container;
        });

        if (!this._widget) {
            return;
        }

        this._setSolutionInputValidation();

        this._form.addEventListener('submit', this._callbacks.onFormSubmit);

        this._widget.addEventListener('frc:widget.statechange', this._callbacks.onV2StateChange);
    }

    _onFormSubmit() {
        this._setSolutionInputValidation();

        if (!this._isWidgetValid()) {
            this._clearSolutionInput();

            this._container.classList.add(this.options.errorClass);

            return;
        }

        this._container.classList.remove(this.options.errorClass);
    }

    _onV1Done(solution) {
        if (!solution) {
            return;
        }

        this._container.classList.remove(this.options.errorClass);
        this._container.classList.add(this.options.successClass);
    }

    _onV1Ready() {
        this._setSolutionInputValidation(true);
    }

    _onV2StateChange(event) {
        const state = event?.detail?.state;
        const mode = event?.detail?.mode;
        this._setSolutionInputValidation();

        if (state !== 'completed') {
            this._clearSolutionInput();
        }

        this._container.classList.remove(this.options.errorClass);
        this._container.classList.remove(this.options.successClass);

        if (state === 'completed') {
            this._container.classList.add(this.options.successClass);
        }

        if (state === 'error') {
            this._container.classList.add(this.options.errorClass);
        }

        if (this.options.invisible && mode === 'interactive') {
            this._showCaptchaWidget();
        }

        if (this.options.invisible && state === 'requesting') {
            this._clearInvisibleCaptchaTimeout();
            this._invisibleShowTimeout = window.setTimeout(() => {
                this._showCaptchaWidget();
            }, this.options.invisibleShowAfterLongWaitMs);
        }

        if (this.options.invisible && state === 'completed') {
            this._clearInvisibleCaptchaTimeout();
        }

        if (this.options.invisible && (state === 'error' || state === 'expired')) {
            this._clearInvisibleCaptchaTimeout();
            this._showCaptchaWidget();
        }
    }

    _isWidgetValid() {
        if (!this._widget) {
            return false;
        }

        if (typeof this._widget.valid === 'boolean') {
            return this._widget.valid;
        }

        if (typeof this._widget.getState === 'function') {
            return this._widget.getState() === 'completed';
        }

        const input = this._getSolutionInput();

        return !!input?.value;
    }

    _setSolutionInputValidation(resetValue = false) {
        const input = this._getSolutionInput();
        if (!input) {
            return;
        }

        if (resetValue) {
            input.value = '';
        }

        input.setAttribute('data-validate-hidden', true);
        input.setAttribute('required', true);
        input.setAttribute('type', 'text');
    }

    _getSolutionInput() {
        return this.el.querySelector(`[name="${this.options.solutionFieldName}"]`);
    }

    _clearSolutionInput() {
        const input = this._getSolutionInput();
        if (!input) {
            return;
        }

        input.value = '';
    }

    _removeListeners() {
        if (this._form && this._callbacks?.onFormSubmit) {
            this._form.removeEventListener('submit', this._callbacks.onFormSubmit);
        }

        if (this._widget && this._callbacks?.onV2StateChange) {
            this._widget.removeEventListener('frc:widget.statechange', this._callbacks.onV2StateChange);
        }
    }

    destroy() {
        this._removeListeners();
        this._clearInvisibleCaptchaTimeout();
        this._clearSolutionInput();
        this._clearStateClasses();

        if (this._widget && typeof this._widget.dispose === 'function') {
            this._widget.dispose();
        }

        this._widget = null;
        this._container = null;
        this._form = null;
        this._callbacks = null;
    }

    _createCallbacks() {
        this._callbacks = {
            onFormSubmit: this._onFormSubmit.bind(this),
            onV1Done: this._onV1Done.bind(this),
            onV1Ready: this._onV1Ready.bind(this),
            onV2StateChange: this._onV2StateChange.bind(this),
        };
    }

    _clearStateClasses() {
        if (!this._container) {
            return;
        }

        this._container.classList.remove(this.options.errorClass);
        this._container.classList.remove(this.options.successClass);
    }

    _showCaptchaWidget() {
        if (!this._container) {
            return;
        }

        this._container.classList.remove(this.options.hiddenClass);
    }

    _clearInvisibleCaptchaTimeout() {
        if (!this._invisibleShowTimeout) {
            return;
        }

        window.clearTimeout(this._invisibleShowTimeout);
        this._invisibleShowTimeout = null;
    }
}
